import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Reusable pagination hook
 * Manages pagination state and provides handlers
 * 
 * @param {Object} initialPageInfo - Initial pagination info
 * @returns {Object} Pagination state and handlers
 */
function usePagination(initialPageInfo = {}) {
  const [currentPage, setCurrentPage] = useState(initialPageInfo.currentPage || 1);
  const [pageInfo, setPageInfo] = useState({
    currentPage: initialPageInfo.currentPage || 1,
    pageSize: initialPageInfo.pageSize || 10,
    totalProducts: initialPageInfo.totalProducts || 0,
    totalPages: initialPageInfo.totalPages || 0,
    hasNextPage: initialPageInfo.hasNextPage || false,
    hasPreviousPage: initialPageInfo.hasPreviousPage || false
  });

  // Use ref to always get the latest currentPage value (avoids stale closures)
  const currentPageRef = useRef(currentPage);
  const pageInfoRef = useRef(pageInfo);
  
  // Update refs when state changes
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    pageInfoRef.current = pageInfo;
  }, [pageInfo]);

  // Sync pageInfo.currentPage when currentPage changes (for display only)
  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      currentPage: currentPage
    }));
  }, [currentPage]);

  const updatePageInfo = useCallback((newPageInfo) => {
    // CRITICAL: Use functional update to get the latest currentPage from ref
    // This prevents the API response from overriding user's page selection
    setPageInfo(prev => {
      const latestCurrentPage = currentPageRef.current;
      const updatedInfo = {
        ...prev,
        ...newPageInfo,
        // Force currentPage to match the user's selection, never from API response
        currentPage: latestCurrentPage
      };
      // Recalculate hasNextPage and hasPreviousPage based on current page and total pages
      updatedInfo.hasNextPage = latestCurrentPage < (updatedInfo.totalPages || 0);
      updatedInfo.hasPreviousPage = latestCurrentPage > 1;
      return updatedInfo;
    });
  }, []);

  const goToPage = useCallback((page) => {
    setCurrentPage(Math.max(1, page));
  }, []);

  const goToNextPage = useCallback(() => {
    // Use ref to get latest pageInfo to avoid stale closure
    const latestPageInfo = pageInfoRef.current;
    const latestCurrentPage = currentPageRef.current;
    
    // Check if we can go to next page based on current page and total pages
    if (latestCurrentPage < (latestPageInfo.totalPages || 0)) {
      setCurrentPage(prev => prev + 1);
    }
  }, []);

  const goToPreviousPage = useCallback(() => {
    // Use ref to get latest currentPage to avoid stale closure
    const latestCurrentPage = currentPageRef.current;
    
    // Check if we can go to previous page
    if (latestCurrentPage > 1) {
      setCurrentPage(prev => Math.max(1, prev - 1));
    }
  }, []);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    pageInfo,
    updatePageInfo,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetToFirstPage
  };
}

export default usePagination;

